<script setup>
import { computed, onMounted, ref, watch, nextTick } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';
import MessageApi from 'dashboard/api/inbox/message';
import { useKeyboardNavigableList } from 'dashboard/composables/useKeyboardNavigableList';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  conversationId: { type: Number, required: true },
  searchKey: { type: String, default: '' },
});
const emit = defineEmits(['select']);
const { t } = useI18n();
const members = ref([]);
const loading = ref(true);
const loadError = ref(false);
const stale = ref(false);
const selectedIndex = ref(0);
const listRef = ref(null);
const filtered = computed(() => {
  const query = props.searchKey.trim().toLowerCase();
  return members.value.filter(member =>
    `${member.label} ${member.phone_jid || ''}`.toLowerCase().includes(query)
  );
});

useKeyboardNavigableList({
  items: filtered,
  selectedIndex,
  onSelect: () => emit('select', filtered.value[selectedIndex.value]),
  adjustScroll: () =>
    nextTick(() =>
      listRef.value?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' })
    ),
});
watch(filtered, () => {
  selectedIndex.value = 0;
});

onMounted(async () => {
  try {
    const response = await MessageApi.getWhatsappGroupParticipants(props.conversationId);
    members.value = response.data.participants || [];
    stale.value = Boolean(response.data.stale);
  } catch (_) {
    loadError.value = true;
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <div
    ref="listRef"
    role="listbox"
    class="absolute bottom-full z-50 mb-2 max-h-64 w-72 overflow-y-auto rounded-lg border border-n-weak bg-n-solid-1 p-1 shadow-lg"
  >
    <div v-if="stale" class="p-2 text-xs text-n-slate-10">
      {{ t('CONVERSATION.MENTION.GROUP_SAVED') }}
    </div>
    <div v-if="loading" class="p-2 text-sm">{{ t('CONVERSATION.MENTION.GROUP_LOADING') }}</div>
    <div v-else-if="loadError" class="p-2 text-sm">{{ t('CONVERSATION.MENTION.GROUP_ERROR') }}</div>
    <button
      v-for="(member, index) in filtered"
      :key="member.jid"
      type="button"
      role="option"
      :aria-selected="selectedIndex === index"
      class="flex w-full items-center gap-2 rounded-md p-2 text-left hover:bg-n-alpha-2"
      :class="{ 'bg-n-alpha-2': selectedIndex === index }"
      @mousedown.prevent="emit('select', member)"
      @mouseover="selectedIndex = index"
    >
      <Avatar :name="member.label" :src="member.avatar_url || ''" :size="20" rounded-full />
      <span class="truncate text-sm">{{ member.label }}</span>
    </button>
    <div v-if="!loading && !loadError && !filtered.length" class="p-2 text-sm text-n-slate-10">
      {{ t('CONVERSATION.MENTION.GROUP_EMPTY') }}
    </div>
  </div>
</template>
